import { invoice_state } from '../../../invoicing/data/invoice-states.service';
import { DeviationSeverity } from '../../../../shared/deviations/types';
import {
  InvoicingDashboardData,
  InvoicingDashboardDeviationType,
  InvoiceStatus,
  InvoicesListItem,
  OverdueAging
} from './types';

const INVOICE_STATUS = {
  concept: { id: invoice_state.draft, description: 'Concept' },
  validation: { id: invoice_state.validation, description: 'Validatie' },
  definitive: { id: invoice_state.approved, description: 'Definitief' },
  sent: { id: invoice_state.send, description: 'Verzonden' },
  cancelled: { id: invoice_state.rejected, description: 'Geannuleerd' },
  overdue: { id: invoice_state.overdue, description: 'Achterstallig' },
  paid: { id: invoice_state.paid, description: 'Betaald' }
} as const;

export function createInvoicingMockData(): InvoicingDashboardData {
  const list = createInvoices();
  const paidInvoices = list.filter(item => item.status.id === INVOICE_STATUS.paid.id);
  const outstandingInvoices = list.filter(item => item.invoice.outstanding > 0);
  const overdueInvoices = list.filter(item => item.status.id === INVOICE_STATUS.overdue.id);
  const conceptCount = countStatus(list, INVOICE_STATUS.concept);
  const validationCount = countStatus(list, INVOICE_STATUS.validation);
  const definitiveCount = countStatus(list, INVOICE_STATUS.definitive);
  const sentCount = countStatus(list, INVOICE_STATUS.sent);
  const cancelledCount = countStatus(list, INVOICE_STATUS.cancelled);
  const overdueCount = countStatus(list, INVOICE_STATUS.overdue);
  const paidCount = countStatus(list, INVOICE_STATUS.paid);
  const aging = createAging(overdueInvoices);

  return {
    summary: {
      invoiceable: {
        receiptCount: 23,
        amount: 486.7
      },
      totals: {
        count: list.length,
        totalPrice: sum(list, item => item.invoice.totalPrice)
      },
      paid: {
        count: paidInvoices.length,
        totalPrice: sum(paidInvoices, item => item.invoice.totalPrice)
      },
      outstanding: {
        count: outstandingInvoices.length,
        totalPrice: sum(outstandingInvoices, item => item.invoice.outstanding)
      },
      overdue: {
        count: overdueInvoices.length,
        totalPrice: sum(overdueInvoices, item => item.invoice.outstanding)
      }
    },
    stateInfo: {
      concept: conceptCount,
      validation: validationCount,
      definitive: definitiveCount,
      sent: sentCount,
      cancelled: cancelledCount,
      overdue: overdueCount,
      paid: paidCount
    },
    deviations: [
      {
        type: InvoicingDashboardDeviationType.incompleteDrafts,
        severity: DeviationSeverity.warning,
        count: conceptCount
      },
      {
        type: InvoicingDashboardDeviationType.waitingForReview,
        severity: DeviationSeverity.warning,
        count: validationCount
      },
      {
        type: InvoicingDashboardDeviationType.overdueInvoices,
        severity: DeviationSeverity.blocking,
        count: overdueInvoices.length
      }
    ],
    list,
    outstanding: {
      outstandingTotalPrice: sum(outstandingInvoices, item => item.invoice.outstanding),
      overdue: {
        totalPrice: sum(overdueInvoices, item => item.invoice.outstanding),
        count: overdueInvoices.length,
        aging
      }
    }
  };
}

function createInvoices(): InvoicesListItem[] {
  return [
    createInvoice(24001, 'F-2026-24001', -52, -22, 18450, 0, INVOICE_STATUS.paid, 101, 'Bouwbedrijf Noord B.V.', 501, 'Bouwplaats Noord'),
    createInvoice(24002, 'F-2026-24002', -39, -9, 12680, 0, INVOICE_STATUS.paid, 102, 'Van Dijk Infra', 502, 'Stationsplein'),
    createInvoice(24003, 'F-2026-24003', -24, 6, 9760, 9760, INVOICE_STATUS.sent, 103, 'Aannemersbedrijf De IJssel', 503, 'Kadeversterking'),
    createInvoice(24004, 'F-2026-24004', -15, 15, 24690, 24690, INVOICE_STATUS.sent, 104, 'Projectontwikkeling Centrum', 504, 'Appartementen Centrum'),
    createInvoice(24005, 'F-2026-24005', -40, -10, 8140, 8140, INVOICE_STATUS.overdue, 107, 'Hofman Civiele Techniek', 505, 'Viaduct Oost'),
    createInvoice(24006, 'F-2026-24006', -67, -37, 6350, 6350, INVOICE_STATUS.overdue, 106, 'Betonwerken Oost', 506, 'Bedrijfshal Deventer'),
    createInvoice(24007, 'F-2026-24007', -105, -75, 5340, 5340, INVOICE_STATUS.overdue, 108, 'Stedelijke Ontwikkeling West', 507, 'Woonwijk West'),
    createInvoice(24008, 'F-2026-24008', -3, 27, 11850, 0, INVOICE_STATUS.concept, 105, 'Woningbouw Salland', 508, 'Nieuwbouw Olst'),
    createInvoice(24009, 'F-2026-24009', -5, 25, 16790, 0, INVOICE_STATUS.validation, 101, 'Bouwbedrijf Noord B.V.', 509, 'Distributiecentrum Noord'),
    createInvoice(24010, 'F-2026-24010', -8, 22, 14320, 0, INVOICE_STATUS.definitive, 102, 'Van Dijk Infra', 510, 'Rondweg Zuid'),
    createInvoice(24011, 'F-2026-24011', -12, 18, 8920, 0, INVOICE_STATUS.cancelled, 107, 'Hofman Civiele Techniek', 511, 'Brugrenovatie West')
  ];
}

function createInvoice(
  id: number,
  number: string,
  invoiceDateOffset: number,
  dueDateOffset: number,
  totalPrice: number,
  outstanding: number,
  status: InvoiceStatus,
  customerId: number,
  customerName: string,
  projectId: number,
  projectName: string
): InvoicesListItem {
  return {
    invoice: {
      id,
      number,
      date: dateWithOffset(invoiceDateOffset),
      totalPrice,
      outstanding,
      dueDate: dateWithOffset(dueDateOffset),
      status: { ...status }
    },
    customer: { id: customerId, name: customerName },
    project: { id: projectId, name: projectName },
    status: { ...status }
  };
}

function createAging(overdueInvoices: InvoicesListItem[]): OverdueAging[] {
  const ranges = [
    { rangeFromDays: 1, rangeToDays: 30 },
    { rangeFromDays: 31, rangeToDays: 60 },
    { rangeFromDays: 61, rangeToDays: 90 },
    { rangeFromDays: 91, rangeToDays: 999 }
  ];

  return ranges.map(range => {
    const invoices = overdueInvoices.filter(item => {
      const ageDays = getAgeDays(item.invoice.dueDate);
      return ageDays >= range.rangeFromDays && ageDays <= range.rangeToDays;
    });
    return {
      ...range,
      count: invoices.length,
      totalPrice: sum(invoices, item => item.invoice.outstanding)
    };
  });
}

function countStatus(items: InvoicesListItem[], status: InvoiceStatus): number {
  return items.filter(item => item.status.id === status.id).length;
}

function sum<T>(items: T[], getValue: (item: T) => number): number {
  return Math.round(items.reduce((total, item) => total + getValue(item), 0) * 100) / 100;
}

function dateWithOffset(days: number): string {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + days);
  return formatDate(date);
}

function getAgeDays(dateDMY: string): number {
  const [day, month, year] = dateDMY.split('-').map(Number);
  const dueDate = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((today.getTime() - dueDate.getTime()) / 86400000));
}

function formatDate(date: Date): string {
  return [
    String(date.getDate()).padStart(2, '0'),
    String(date.getMonth() + 1).padStart(2, '0'),
    date.getFullYear()
  ].join('-');
}
